import { useState } from 'react';
import { Settings, Users, History, TrendingUp, Zap } from 'lucide-react';
import GlobalDefaultsTab from '../components/featureflags/GlobalDefaultsTab';
import CompanyOverridesTab from '../components/featureflags/CompanyOverridesTab';
import AuditLogTab from '../components/featureflags/AuditLogTab';
import RolloutProgressTab from '../components/featureflags/RolloutProgressTab';

type TabType = 'global' | 'companies' | 'audit' | 'rollout';

export default function FeatureFlagsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('global');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header with gradient background - matching monitoring page style */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 opacity-90"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Feature Flags</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Manage feature rollouts, observability, and system configurations
          </p>
          <div className="mt-6 flex items-center gap-4 text-white opacity-70">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Instant rollback</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="text-sm">Company-specific overrides</span>
            </div>
            <div className="flex items-center gap-2">
              <History className="w-4 h-4" />
              <span className="text-sm">Full audit trail</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - matching monitoring page style */}
      <div className="flex gap-2 mb-6 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl w-fit border border-slate-700/50">
        <button
          onClick={() => setActiveTab('global')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'global'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Settings className="w-4 h-4" />
          Global Defaults
        </button>

        <button
          onClick={() => setActiveTab('companies')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'companies'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Users className="w-4 h-4" />
          Company Overrides
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'audit'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <History className="w-4 h-4" />
          Audit Log
        </button>

        <button
          onClick={() => setActiveTab('rollout')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'rollout'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Rollout Progress
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'global' && <GlobalDefaultsTab key={refreshKey} onRefresh={handleRefresh} />}
      {activeTab === 'companies' && <CompanyOverridesTab key={refreshKey} onRefresh={handleRefresh} />}
      {activeTab === 'audit' && <AuditLogTab key={refreshKey} />}
      {activeTab === 'rollout' && <RolloutProgressTab key={refreshKey} />}
    </div>
  );
}
